import * as React from "react";
import {
  apiPost,
  buildFilterString,
  buildSearchString,
  fetchData,
} from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import {
  KeyValue,
  Query,
  SimpleTableRow,
  SortDirection,
  TableHeader,
} from "../../../interfaces";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../../MultiHeaderTable/SimpleTableBody";
import { useHistory, useLocation } from "react-router-dom";
import ResultPagination from "../../../containers/ResultPagination";
import { Divider, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  removeUrlParameter,
  fileNameFormat,
} from "../../../common/services/Utilities";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import MultiTableBody from "../../MultiHeaderTable/MultiTableBody";
import LoadingIndicator from "../../LoadingIndicator";
import useSortProperties from "../../../hooks/useSortProperties";
import {
  getStateName,
  selectedStatesAlpha,
} from "../../../refdata/FilterOptions";

interface Props {
  appState: Query;
  updateSearch: (field: string, value: string) => void;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateUi: (field: string, value: string) => void;
  searchPills: React.ReactElement;
}
const useStyles = makeStyles((theme: Theme) => ({
  loadingSpinner: {
    marginTop: 24,
    width: "100%",
    textAlign: "center",
  },
  loadingText: {
    fontSize: "1.5rem",
    width: "100%",
    textAlign: "center",
    height: "100%",
  },
}));

const bankNameHeaderMetaData: KeyValue[] = [
  {
    title: "Institution Name",
    apiField: "NAMEFULL",
    styles: { textAlign: "left" },
  },
  { title: "Cert", apiField: "CERT", styles: {} },
  { title: "Docket", apiField: "DOCKET", styles: {} },
  { title: "Class", apiField: "BKCLASS", styles: {} },
  { title: "City (USPS)", apiField: "CITY2BR", styles: {} },
  { title: "County", apiField: "CNTYNAMB", styles: {} },
  { title: "State", apiField: "STALP", styles: {} },
  { title: "Total Branch Offices", apiField: "", styles: {} },
  {
    title: "Total Deposits (Thousands USD)",
    apiField: "",
    styles: { textAlign: "right" },
  },
];
const grwothRatebankNameHeaderMetaData: KeyValue[] = [
  {
    title: "Institution Name",
    apiField: "NAMEFULL",
    styles: { textAlign: "left" },
  },
  { title: "Cert", apiField: "CERT", styles: {} },
  { title: "Docket", apiField: "DOCKET", styles: {} },
  { title: "Class", apiField: "BKCLASS", styles: {} },
  { title: "City (USPS)", apiField: "CITY2BR", styles: {} },
  { title: "County", apiField: "CNTYNAMB", styles: {} },
  { title: "State", apiField: "STALP", styles: {} },
];
const bhcsNameHeaderMetaData: KeyValue[] = [
  {
    title: "Bank Holding Company Name / Insured Subsidiary Name",
    apiField: "NAMEHCR",
    secondaryApiField: "NAMEFULL",
    styles: { textAlign: "left" },
  },
  {
    title: "RSSDHCR / CERT",
    apiField: "RSSDHCR",
    secondaryApiField: "CERT",
    styles: {},
  },
  {
    title: "State",
    apiField: "STALPHCR",
    secondaryApiField: "STALP",
    styles: {},
  },
  {
    title: "City",
    apiField: "CITYHCR",
    secondaryApiField: "CITY",
    styles: {},
  },
];
const transformedData = (tableData: KeyValue[]) => {
  return tableData.map((data: KeyValue) => {
    const tableCellData: SimpleTableRow = {};
    [
      {
        title: "Total Deposits (Thousands USD)",
        apiField: "sum_DEPSUMBR",
      },
      { title: "Total Branch Offices", apiField: "count" },
    ].forEach((cell: KeyValue) => {
      tableCellData[cell.title] = {
        value:
          (cell.apiField === "sum_DEPSUMBR" && data[cell.apiField]) ||
          (cell.apiField === "count" && data[cell.apiField])
            ? data[cell.apiField].toLocaleString()
            : data[cell.apiField],
        styles: cell.apiField === "sum_DEPSUMBR" ? { textAlign: "right" } : {},
      };
    });
    return tableCellData;
  });
};
export default function DepositsNameSearchTable({
  appState,
  searchPills,
  updateSort,
  updateSearch,
  updateUi,
}: Props) {
  const sortProperties = useSortProperties(appState, updateSort);

  const location = useLocation();
  appState.filters.stalpbr =
    appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
      ? appState.filters.stalpbr
      : "";
  const headerMetaData =
    appState.ui.institutionType === "banks" &&
    appState.ui.reportType === "growthRate"
      ? grwothRatebankNameHeaderMetaData
      : appState.ui.institutionType === "banks" &&
        appState.ui.reportType !== "growthRate"
      ? bankNameHeaderMetaData
      : bhcsNameHeaderMetaData;
  const nameTableHeaders: TableHeader[][] = [
    headerMetaData.map(element => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];
  const history = useHistory();
  const classes = useStyles();
  const newAppState = React.useMemo(() => {
    return {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      pagination: appState.pagination,
    };
  }, [appState.filters, appState.pagination, appState.search, appState.sort]);
  const [previousAppState, setPreviousAppState] = React.useState<KeyValue>({});
  const [sortableData, setSortableData] = React.useState<KeyValue[]>([{}]);
  const [data, setData] = React.useState<KeyValue[]>([{}]);
  const [secondaryDataCall, setSecondaryDataCall] = React.useState(false);
  const [secondaryData, setSecondaryData] = React.useState<KeyValue[][]>([
    [{}],
  ]);
  const [loading, setLoading] = React.useState(false);
  const [recordCount, setRecordCount] = React.useState(0);
  const [certs, setCerts] = React.useState<string[]>([]);
  const [fetchAgg, setFetchAgg] = React.useState(false);
  const [
    previousMarketSelectCerts,
    setPreviousMarketSelectCerts,
  ] = React.useState<number[]>([]);
  const [displayError, setDisplayError] = React.useState(false);
  React.useEffect(() => {
    const marketSelectCerts =
      appState.ui.marketSelection && appState.ui.marketSelection[0].certUninums
        ? [].concat
            .apply(
              [],
              appState.ui.marketSelection.map((market: KeyValue) =>
                Object.keys(market.certUninums),
              ),
            )
            .flat()
        : undefined;
    const rowsPerPage = appState.pagination.resultLimit
      ? appState.pagination.resultLimit
      : "25";

    const page = appState.pagination.pageNumber
      ? appState.pagination.pageNumber
      : 1;
    if (
      JSON.stringify(newAppState) !== JSON.stringify(previousAppState) ||
      (marketSelectCerts &&
        JSON.stringify(marketSelectCerts) !==
          JSON.stringify(previousMarketSelectCerts))
    ) {
      if (
        marketSelectCerts &&
        JSON.stringify(marketSelectCerts) !==
          JSON.stringify(previousMarketSelectCerts)
      ) {
        setPreviousMarketSelectCerts(marketSelectCerts);
      }
      setData([{}]);
      setLoading(true);
      setPreviousAppState(newAppState);

      const marketCerts =
        marketSelectCerts && marketSelectCerts.length > 0
          ? `CERT:(${marketSelectCerts.join(" OR ")}) AND `
          : "";

      const previousName = previousAppState?.search?.name;
      const previousBhcName = previousAppState?.search?.bhcName;

      const sortCheck =
        appState.search.name !== previousName ||
        appState.search.bhcName !== previousBhcName;
      if (sortCheck) {
        updateSort("", "");
      }
      const filters: KeyValue = { ...appState.filters, lastYear: "" };
      const yearDates = appState.filters.startDate
        ? `YEAR:(${appState.filters.startDate} OR ${appState.filters.endDate})`
        : `YEAR:${appState.filters.lastYear}`;
      const filtersString = buildFilterString(filters)
        ? `${buildFilterString(filters)} AND `
        : "";
      const nameSearchPostReq = apiPost({
        search:
          appState.ui.institutionType === "bhcs"
            ? `NAMEHCR:${appState.search.bhcName}`
            : `NAME:${appState.search.name}`,

        filters: `${filtersString}${marketCerts}${yearDates}`,
        sort_by: sortCheck ? "" : appState.sort.sortField,
        sort_order: sortCheck ? "" : appState.sort.sortOrder.toLowerCase(),
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
        get_unique: appState.search.name ? "CERT" : "RSSDHCR",
      });
      (async () => {
        await fetchData(`${ApiBaseUrl()}/sod`, nameSearchPostReq)
          .then(response => {
            if (response.totals.count === 0) {
              setLoading(false);
              setDisplayError(true);
            } else {
              !loading && setLoading(true);
              displayError && setDisplayError(false);
            }
            setRecordCount(response.totals.count);

            setCerts(response.data.map((data: KeyValue) => data.CERT));
            const transformedData = (tableData: KeyValue[]) => {
              return tableData.map((data: KeyValue) => {
                const tableCellData: SimpleTableRow = {};
                const headerData =
                  appState.ui.institutionType === "banks" &&
                  appState.ui.reportType === "growthRate"
                    ? grwothRatebankNameHeaderMetaData
                    : appState.ui.institutionType === "banks" &&
                      appState.ui.reportType !== "growthRate"
                    ? bankNameHeaderMetaData
                    : bhcsNameHeaderMetaData;
                headerData.forEach((value: KeyValue) => {
                  const apiField = value.apiField;
                  tableCellData[apiField ? apiField : value.title] = {
                    value:
                      apiField === "DEPSUMBR" && data[apiField]
                        ? data[apiField].toLocaleString()
                        : data[apiField],
                    styles: value.styles,
                  };
                  tableCellData.rowOnClick = () => {
                    appState.ui.institutionType === "banks"
                      ? history.push({
                          search: `${
                            removeUrlParameter(window.location.href, [
                              "name",
                              "bhcName",
                              "pageNumber",
                              "resultLimit",
                            ]).search
                          }&cert=${data.CERT}`,
                          state: {
                            appState: appState,
                            backResults: `${location.pathname}${location.search}`,
                          },
                        })
                      : history.push({
                          search: `${
                            removeUrlParameter(window.location.href, [
                              "name",
                              "bhcName",
                              "pageNumber",
                              "resultLimit",
                            ]).search
                          }&bhcRSSD=${data.RSSDHCR}`,
                          state: {
                            appState: appState,
                            backResults: `${location.pathname}${location.search}`,
                          },
                        });
                    updateSearch("name", "");
                    updateSearch("bhcName", "");
                    updateUi(
                      "updateFormField",
                      appState.ui.institutionType === "banks"
                        ? "cert"
                        : "bhcRSSD",
                    );
                  };
                });

                return tableCellData;
              });
            };
            appState.ui.institutionType === "banks"
              ? setSortableData(transformedData(response.data))
              : setData(transformedData(response.data));
            if (appState.ui.institutionType !== "banks") {
              setSecondaryDataCall(true);
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            appState.ui.institutionType === "banks" && setFetchAgg(true);
          });
      })();
    }
  }, [
    appState,
    displayError,
    history,
    location,
    loading,
    newAppState,
    previousAppState,
    previousMarketSelectCerts,
    recordCount,
    updateSearch,
    updateSort,
    updateUi,
  ]);
  const dateHeaders = appState.filters.startDate
    ? { label: "Starting/ Ending Period", key: "YEAR" }
    : { label: "Report Period", key: "YEAR" };
  const downloadHeaders = appState.search.bhcName
    ? [
        dateHeaders,
        { label: "Institution Name", key: "NAMEFULL" },
        { label: "Cert", key: "CERT" },
        { label: "State", key: "STALP" },
        { label: "City", key: "CITY2BR" },
        { label: "Bank Holding Company Name", key: "NAMEHCR" },
        { label: "RRSDHCR", key: "RSSDHCR" },
        { label: "BHC State", key: "STALPHCR" },
        {
          label: " BHC City",
          key: "CITYHCR",
        },
      ]
    : [
        dateHeaders,
        { label: "Institution Name", key: "NAMEFULL" },
        { label: "Cert", key: "CERT" },
        { label: "Docket", key: "DOCKET" },
        { label: "Class", key: "BKCLASS" },
        { label: "City (USPS)", key: "CITY2BR" },
        { label: "County", key: "CNTYNAMB" },
        { label: "State", key: "STALP" },
        { label: "Total Branch Offices", key: "count" },
        { label: "Total Deposits (Thousands USD)", key: "sum_DEPSUMBR" },
      ];
  const filters: KeyValue = { ...appState.filters, lastYear: "" };
  const yearDates = appState.filters.startDate
    ? `YEAR:(${appState.filters.startDate} OR ${appState.filters.endDate})`
    : `YEAR:${appState.filters.lastYear}`;
  const filtersString = buildFilterString(filters)
    ? `${buildFilterString(filters)} AND `
    : "";
  const downloadUrl = appState.search.name
    ? {
        multiDownload: [
          `${ApiBaseUrl()}/sod?filters=${filtersString}${yearDates}&search=${buildSearchString(
            appState.search,
          )}&get_unique=CERT`,
          `${ApiBaseUrl()}/sod?filters=${filtersString}${yearDates}&search=${buildSearchString(
            appState.search,
          )}&agg_by=CERT&agg_sum_fields=DEPSUMBR`,
        ],
        combineByKey: "CERT",
      }
    : {
        downloadUrl: `${ApiBaseUrl()}/sod?filters=${filtersString}${yearDates}&get_unique=CERT&search=${buildSearchString(
          appState.search,
        )}`,
      };
  const selectedMarkets: string[][] = [];

  if (
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
  ) {
    for (let i = 0; i < appState.filters.stateNames.length; i++) {
      const market = [
        getStateName(selectedStatesAlpha, appState.filters.stateNames[i]),
        appState.ui.marketSelection[i]?.counties?.join(", "),
        appState.ui.marketSelection[i]?.cities?.join(", "),
        appState.ui.marketSelection[i]?.zips?.join(", "),
      ];
      selectedMarkets.push(market);
    }
  }
  const selectedMarket =
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
      ? [
          ["Selected Market"],
          ["State", "Counties", "Cities", "ZIP Codes"],
          ...selectedMarkets,
          [],
        ]
      : undefined;
  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        `SOD_Name_Search_${
          appState.search.name ? appState.search.name : appState.search.bhcName
        }`,
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      ...downloadUrl,
      headers: downloadHeaders,
      additionalContent: selectedMarket,
    },
  ];
  const download = (
    <ApiDataDownload
      app="SOD"
      apiData={apiData}
      commaSeparatedNumbers={["count", "sum_DEPSUMBR"]}
    />
  );

  React.useEffect(() => {
    if (certs.length > 0 && fetchAgg) {
      setFetchAgg(false);
      const filters = { ...appState.filters, lastYear: "" };
      const yearDates = appState.filters.startDate
        ? `YEAR:(${appState.filters.startDate} OR ${appState.filters.endDate})`
        : `YEAR:${appState.filters.lastYear}`;
      const filtersString = buildFilterString(filters)
        ? `${buildFilterString(filters)} AND `
        : "";
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          apiPost({
            filters: `${filtersString}${yearDates} AND CERT:(${certs.join(
              " OR ",
            )})`,
            search: buildSearchString(appState.search),
            agg_by: "CERT",
            agg_sum_fields: "DEPSUMBR",
          }),
        )
          .then(response => {
            const newData = sortableData.map(
              (dataset: KeyValue, index: number) => {
                const cert = dataset.CERT.value;
                const currentData = response.data.filter((data: KeyValue) => {
                  return data.CERT === cert;
                });
                if (
                  appState.ui.institutionType === "banks" &&
                  appState.ui.reportType === "growthRate"
                ) {
                  return { ...dataset };
                } else {
                  return { ...dataset, ...transformedData(currentData)[0] };
                }
              },
            );

            setData(newData);
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => setLoading(false));
      })();
    }
  }, [
    appState.filters,
    appState.search,
    appState.ui.institutionType,
    appState.ui.reportType,
    certs,
    fetchAgg,
    sortableData,
  ]);
  React.useEffect(() => {
    if (secondaryDataCall) {
      setSecondaryDataCall(false);
      const filters = { ...appState.filters, lastYear: "" };
      const yearDates = appState.filters.startDate
        ? ` AND YEAR:(${appState.filters.startDate} OR ${appState.filters.endDate})`
        : ` AND YEAR:${appState.filters.lastYear}`;
      const filtersString = buildFilterString(filters)
        ? `${buildFilterString(filters)} AND `
        : "";
      const fetches = data.map(val => {
        return `${ApiBaseUrl()}/sod?filters=${filtersString}RSSDHCR:${
          val.RSSDHCR.value
        }${yearDates}&search=${buildSearchString(
          appState.search,
        )}&get_unique=CERT`;
      });

      (async () => {
        await Promise.all(fetches.map((url: string) => fetchData(url)))
          .then(response => {
            const transformedSecondaryData = (tableData: KeyValue[][]) => {
              return tableData.map((data: KeyValue[], index) => {
                const tableCellData: SimpleTableRow[] = [];
                const multiLevelData = data.map((val: KeyValue) => {
                  return bhcsNameHeaderMetaData.map((cell: KeyValue) => {
                    return (tableCellData[cell.title] = {
                      value: val[cell.secondaryApiField],
                      styles:
                        cell.secondaryApiField === "NAMEFULL"
                          ? { textAlign: "left", paddingLeft: "2rem" }
                          : {},
                    });
                  });
                });

                return multiLevelData;
              });
            };
            const data = response.map(res => res.data);
            setSecondaryData(transformedSecondaryData(data));
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters,
    appState.search,
    appState.ui.institutionType,
    data,
    secondaryDataCall,
  ]);

  return (
    <>
      <Typography id="name-search-count" variant="h2">
        {recordCount ? recordCount.toLocaleString() : ""}{" "}
        {!recordCount
          ? "No Results"
          : recordCount === 1
          ? "Matching Result"
          : "Matching Results"}
      </Typography>
      {appState.ui.reportType !== "growthRate" && (
        <div style={{ position: "relative", right: 0, top: 5 }}>{download}</div>
      )}
      <Divider />
      {searchPills}
      {displayError ? (
        <Typography variant="body2" color="primary">
          Double-check your search criteria for errors, or try your search again
          limiting or removing location-based criteria.
        </Typography>
      ) : (
        <ResultPagination count={recordCount}>
          {loading ? (
            <LoadingIndicator
              text="Loading"
              spinnerSize={32}
              textClass={classes.loadingText}
              spinnerClass={classes.loadingSpinner}
            />
          ) : (
            <MultiHeaderTable
              sortProperties={sortProperties}
              tableBody={
                appState.ui.institutionType === "banks" ? (
                  <SimpleTableBody tableData={data} />
                ) : (
                  <MultiTableBody
                    tableData={data}
                    secondaryTableData={secondaryData}
                  />
                )
              }
              tableHeaders={nameTableHeaders}
            />
          )}
        </ResultPagination>
      )}
    </>
  );
}
